/* eslint-disable max-lines */
// src/components/AudioRecorder/RecorderMetadataModal.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  LinearProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  GraphicEq as GraphicEqIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useRecorder } from '../../contexts/RecorderContext';
import { useEntityContext } from '../EntityContextSwitch';
import api from '../../services/api';
import AudioPlayer from './AudioPlayer';

// Constants
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const AVAILABLE_ROLES = [
  { id: 'admin', label: 'Admin', description: 'Full entity access' },
  { id: 'reports', label: 'Reports', description: 'Report generation and viewing' },
  { id: 'data', label: 'Data', description: 'Data management access' },
  { id: 'viewer', label: 'Viewer', description: 'Read-only access' },
];

// eslint-disable-next-line max-lines-per-function
const RecorderMetadataModal = () => {
  const {
    showMetadataModal,
    audioBlob,
    audioUrl,
    duration,
    recordingEntityId,
    recordingEntityName,
    discardRecording,
    onSaveComplete,
    getRecorderService,
  } = useRecorder();

  // Get current entity from context as fallback
  const { currentEntity } = useEntityContext();

  // Form state
  const [noteTitle, setNoteTitle] = useState('');
  const [recordingDate, setRecordingDate] = useState('');
  const [callDescription, setCallDescription] = useState('');
  const [noteVisibility, setNoteVisibility] = useState('private');
  const [noteAllowedRoles, setNoteAllowedRoles] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  // Entity selection state
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [selectedEntityName, setSelectedEntityName] = useState('');
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Discard confirmation state
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Get current date/time in local timezone for datetime-local input
  const getCurrentLocalDateTime = useCallback(() => {
    const now = new Date();
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, []);

  // Initialize form when modal opens
  useEffect(() => {
    if (showMetadataModal) {
      // Generate default title with timestamp
      const timestamp = new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
      setNoteTitle(`Audio Note - ${timestamp}`);

      // Set entity - prefer recordingEntityId, fall back to current context
      const entityId = recordingEntityId || currentEntity?.id || '';
      const entityName = recordingEntityName || currentEntity?.name || '';

      console.log('RecorderMetadataModal: Initializing with entity', {
        recordingEntityId,
        recordingEntityName,
        currentEntityId: currentEntity?.id,
        currentEntityName: currentEntity?.name,
        usingEntityId: entityId,
        usingEntityName: entityName
      });

      setSelectedEntityId(entityId);
      setSelectedEntityName(entityName);

      // Auto-set recording date to current date/time
      setRecordingDate(getCurrentLocalDateTime());

      // Reset other fields
      setNoteVisibility('private');
      setNoteAllowedRoles([]);
      setTags([]);
      setCallDescription('');
    }
  }, [showMetadataModal, recordingEntityId, recordingEntityName, currentEntity, getCurrentLocalDateTime]);

  // Load entities for dropdown
  useEffect(() => {
    const loadEntities = async () => {
      setLoadingEntities(true);
      try {
        const response = await api.getEntities({ status: 'active' });
        console.log('RecorderMetadataModal: API response for entities:', response);

        // Handle API response - entities are in response.entities
        let entityList = [];
        if (response && response.entities && Array.isArray(response.entities)) {
          entityList = response.entities;
        } else if (Array.isArray(response)) {
          entityList = response;
        }

        console.log('RecorderMetadataModal: Loaded entities:', entityList.length, 'items');
        setEntities(entityList);

        // If we have an entityId from context, ensure it's selected
        const entityIdToUse = recordingEntityId || currentEntity?.id;
        console.log('RecorderMetadataModal: Looking for entity:', entityIdToUse);

        if (entityIdToUse && entityList.length > 0) {
          const found = entityList.find(e => e.id === entityIdToUse);
          if (found) {
            console.log('RecorderMetadataModal: Found and selecting entity:', found.name);
            setSelectedEntityId(found.id);
            setSelectedEntityName(found.name);
          } else {
            console.log('RecorderMetadataModal: Entity not found in list');
          }
        }
      } catch (error) {
        console.error('Failed to load entities:', error);
        toast.error('Failed to load entities');
      } finally {
        setLoadingEntities(false);
      }
    };

    if (showMetadataModal) {
      loadEntities();
    }
  }, [showMetadataModal, recordingEntityId, currentEntity]);

  // Load existing tags from entity notes
  useEffect(() => {
    const loadTags = async () => {
      if (!selectedEntityId) return;

      try {
        const notes = await api.listEntityNotes(selectedEntityId);
        const noteList = notes.notes || notes || [];
        const allTags = noteList.flatMap((n) => n.tags || []);
        const uniqueTags = [...new Set(allTags)];
        setTagOptions(uniqueTags);
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };

    if (showMetadataModal && selectedEntityId) {
      loadTags();
    }
  }, [showMetadataModal, selectedEntityId]);

  // Visibility icon helper
  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private':
        return <PersonIcon fontSize="small" />;
      case 'public':
        return <PublicIcon fontSize="small" />;
      case 'team':
        return <GroupIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  // File size display
  const fileSizeDisplay = useMemo(() => {
    if (!audioBlob) return '0 KB';
    if (audioBlob.size < 1024 * 1024) {
      return `${(audioBlob.size / 1024).toFixed(1)} KB`;
    }
    return `${(audioBlob.size / 1024 / 1024).toFixed(2)} MB`;
  }, [audioBlob]);

  // Save handler
  // eslint-disable-next-line max-lines-per-function
  const handleSave = async () => {
    if (!audioBlob || !selectedEntityId) return;

    setSaving(true);
    setUploadProgress(10);

    try {
      // Check file size
      if (audioBlob.size > MAX_FILE_SIZE) {
        const sizeMB = audioBlob.size / 1024 / 1024;
        toast.error(
          `Audio file is too large (${sizeMB.toFixed(1)} MB). Maximum size is ${MAX_FILE_SIZE / 1024 / 1024} MB.`
        );
        setSaving(false);
        return;
      }

      // Compress large files if possible
      let audioData = audioBlob;
      let isCompressed = false;
      const sizeMB = audioBlob.size / 1024 / 1024;

      if (sizeMB > 25) {
        toast.info('Compressing audio...');
        setUploadProgress(15);
        const recorderService = getRecorderService();
        if (recorderService?.compressAudio) {
          // eslint-disable-next-line max-depth
          try {
            const compressedBlob = await recorderService.compressAudio(audioBlob);
            // eslint-disable-next-line max-depth
            if (compressedBlob.size < audioBlob.size) {
              audioData = compressedBlob;
              isCompressed = true;
              toast.info(
                `Compressed from ${sizeMB.toFixed(1)} MB to ${(compressedBlob.size / 1024 / 1024).toFixed(1)} MB`
              );
            }
          } catch (e) {
            console.warn('Could not compress audio:', e);
          }
        }
      }

      setUploadProgress(25);

      // Convert to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioData);

      const base64Audio = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
      });

      // Generate filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `audio_recording_${timestamp}.${audioData.type?.split('/')[1] || 'webm'}`;

      setUploadProgress(40);
      toast.info('Uploading to cloud storage...');

      // Upload to GCS (no transcription field — server-side Whisper handles it)
      const uploadResult = await api.uploadAudioToGCS({
        entityId: selectedEntityId,
        audioData: base64Audio,
        mimeType: audioData.type || 'audio/webm',
        fileName,
        metadata: {
          duration,
          recordedAt: recordingDate || new Date().toISOString(),
          compressed: isCompressed,
          originalSize: audioBlob.size,
          compressedSize: audioData.size,
          title: noteTitle,
          callDescription,
        },
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || 'Failed to upload audio');
      }

      setUploadProgress(75);
      toast.info('Creating note...');

      // Note content — just the recording metadata. Transcription status is
      // determined by audioMetadata.transcription field, not by HTML markers.
      const noteContent = `
        <div class="audio-note">
          <p style="color: #888; font-size: 0.85em;"><strong>${noteTitle}</strong></p>
          <p style="color: #888; font-size: 0.85em;">Duration: ${duration} | Recorded: ${recordingDate ? new Date(recordingDate).toLocaleString() : new Date().toLocaleString()}</p>
          ${callDescription ? `<p style="color: #888; font-size: 0.85em; white-space: pre-wrap;">${callDescription}</p>` : ''}
        </div>
      `;

      // Build tags array - only semantic tags, no system metadata tags
      const noteTags = [
        'audio-note',
        'voice-recording',
        ...tags,
      ];

      // Create note
      const noteData = {
        entityId: selectedEntityId,
        content: noteContent,
        tags: noteTags,
        visibility: noteVisibility,
        allowedRoles: noteVisibility === 'team' ? noteAllowedRoles : undefined,
        metadata: {
          audioGcsPath: uploadResult.gcsPath,
          audioMetadataPath: uploadResult.metadataPath,
          audioFileSize: uploadResult.fileSize,
        },
      };

      const result = await api.createManualNote(noteData);

      setUploadProgress(100);

      if (result && (result.id || result.success || result.noteId)) {
        onSaveComplete();
      } else {
        throw new Error(result?.error || 'Failed to save note');
      }
    } catch (error) {
      console.error('Failed to save audio:', error);
      toast.error('Failed to save audio: ' + error.message);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleDiscard = () => {
    setShowDiscardConfirm(true);
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    discardRecording();
  };

  const handleEntityChange = (event) => {
    const entityId = event.target.value;
    setSelectedEntityId(entityId);
    const entity = entities.find((e) => e.id === entityId);
    setSelectedEntityName(entity?.name || '');
  };

  if (!showMetadataModal) return null;

  return (
    <>
      <Dialog
        open={showMetadataModal}
        onClose={() => {}} // Prevent clicking outside to close
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={saving}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GraphicEqIcon color="primary" />
            <Typography variant="h6">Save Audio Note</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Upload Progress */}
          {saving && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {uploadProgress < 40 ? 'Preparing...' : uploadProgress < 75 ? 'Uploading...' : 'Saving...'}
              </Typography>
            </Box>
          )}

          {/* Audio Player */}
          <Box sx={{ mb: 2 }}>
            <AudioPlayer
              src={audioUrl}
              duration={duration}
              fileSize={fileSizeDisplay}
            />
          </Box>

          {/* Server-side Transcription Notice */}
          <Alert severity="info" sx={{ mb: 2 }}>
            Transcription will be generated automatically after save.
          </Alert>

          {/* Entity Selection */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              value={selectedEntityId}
              onChange={handleEntityChange}
              label="Entity"
              disabled={loadingEntities || saving}
            >
              {loadingEntities ? (
                <MenuItem value="" disabled>
                  Loading entities...
                </MenuItem>
              ) : entities.length === 0 ? (
                <MenuItem value="" disabled>
                  No entities available
                </MenuItem>
              ) : (
                entities.map((entity) => (
                  <MenuItem key={entity.id} value={entity.id}>
                    {entity.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Title */}
          <TextField
            fullWidth
            label="Title"
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
            sx={{ mb: 2 }}
            required
            disabled={saving}
          />

          {/* Recording Date - Auto-populated */}
          <TextField
            fullWidth
            label="Recording Date & Time"
            type="datetime-local"
            value={recordingDate}
            onChange={(e) => setRecordingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            disabled={saving}
          />

          {/* Call Description */}
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={2}
            value={callDescription}
            onChange={(e) => setCallDescription(e.target.value)}
            placeholder="Brief description of the recording..."
            sx={{ mb: 2 }}
            disabled={saving}
          />

          {/* Tags */}
          <Autocomplete
            multiple
            freeSolo
            options={tagOptions}
            value={tags}
            onChange={(_, newValue) => setTags(newValue)}
            disabled={saving}
            renderInput={(params) => (
              <TextField {...params} label="Tags" placeholder="Add tags..." sx={{ mb: 2 }} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
              ))
            }
          />

          {/* Visibility */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={noteVisibility}
              onChange={(e) => setNoteVisibility(e.target.value)}
              label="Visibility"
              disabled={saving}
              startAdornment={getVisibilityIcon(noteVisibility)}
            >
              <MenuItem value="private">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  Private (Only you and system admins)
                </Box>
              </MenuItem>
              <MenuItem value="team">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon fontSize="small" />
                  Team (Selected roles)
                </Box>
              </MenuItem>
              <MenuItem value="public">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon fontSize="small" />
                  Public (All entity members)
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Role Selection for Team Visibility */}
          {noteVisibility === 'team' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Allowed Roles</InputLabel>
              <Select
                multiple
                value={noteAllowedRoles}
                onChange={(e) => setNoteAllowedRoles(e.target.value)}
                label="Allowed Roles"
                disabled={saving}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={AVAILABLE_ROLES.find((r) => r.id === value)?.label || value}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {AVAILABLE_ROLES.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Box>
                      <Typography variant="body2">{role.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Entity Info */}
          <Alert severity="info">
            This audio note will be saved to: <strong>{selectedEntityName || 'Please select an entity'}</strong>
            {noteVisibility === 'public' && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }} color="warning.main">
                Public notes are visible to all entity members
              </Typography>
            )}
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleDiscard}
            disabled={saving}
            startIcon={<DeleteIcon />}
            color="error"
          >
            Discard
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !noteTitle.trim() || !selectedEntityId}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard Confirmation Dialog */}
      <Dialog open={showDiscardConfirm} onClose={() => setShowDiscardConfirm(false)}>
        <DialogTitle>Discard Recording?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to discard this recording? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardConfirm(false)}>Keep Recording</Button>
          <Button onClick={confirmDiscard} color="error" variant="contained">
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RecorderMetadataModal;
